import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { userProfileApi } from '@/lib/api/user-profile';
import { toast } from '@/components/ui/use-toast';
import { User, Users, Heart } from 'lucide-react';
import { VStack, HStack, Grid } from '@/components/core';

interface OnboardingModalProps {
  open: boolean;
  onComplete: () => void;
}

type Gender = 'male' | 'female';
type Orientation = 'Hetero' | 'Schwul' | 'Bi';

export function OnboardingModal({ open, onComplete }: OnboardingModalProps) {
  const { t } = useTranslation('common');
  const [step, setStep] = useState<1 | 2>(1);
  const [gender, setGender] = useState<Gender | null>(null);
  const [orientation, setOrientation] = useState<Orientation | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleGenderSelect = (selectedGender: Gender) => {
    setGender(selectedGender);
    setStep(2);
  };

  const handleOrientationSelect = async (selectedOrientation: Orientation) => {
    setOrientation(selectedOrientation);
    setIsSubmitting(true);

    try {
      // First save gender and orientation
      await userProfileApi.updateMyProfile({
        gender: gender!,
        sexual_orientation: selectedOrientation,
      });

      // Then mark onboarding as completed
      await userProfileApi.completeOnboarding();

      onComplete();
    } catch (error) {
      console.error('Failed to save onboarding preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to save preferences. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getOrientationLabel = (ori: Orientation): string => {
    switch (ori) {
      case 'Hetero':
        return gender === 'male' ? t('onboarding.hetero') : t('onboarding.schwul');
      case 'Schwul':
        return gender === 'male' ? t('onboarding.schwul') : t('onboarding.hetero');
      case 'Bi':
        return t('onboarding.bi');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/95" />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md mx-4 bg-muted rounded-2xl shadow-token-modal overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-brand-primary via-brand-secondary to-brand-secondary p-token-xl text-center">
          <HStack justify="center" className="mb-token-md">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
              <Heart className="w-8 h-8 text-white" />
            </div>
          </HStack>
          <h2 className="text-2xl font-bold text-white mb-token-sm">
            {t('onboarding.title')}
          </h2>
          <p className="text-white/80">
            {t('onboarding.subtitle')}
          </p>
        </div>

        {/* Step Indicator */}
        <HStack justify="center" gap="sm" className="py-token-md bg-muted">
          <div className={`w-2 h-2 rounded-full transition-colors ${step >= 1 ? 'bg-brand-primary' : 'bg-muted-foreground'}`} />
          <div className={`w-2 h-2 rounded-full transition-colors ${step >= 2 ? 'bg-brand-primary' : 'bg-muted-foreground'}`} />
        </HStack>

        {/* Content */}
        <div className="p-token-lg pt-0 bg-muted">
          {step === 1 && (
            <VStack gap="md">
              <h3 className="text-lg font-semibold text-center text-white mb-token-lg">
                {t('onboarding.step1Title')}
              </h3>

              <Grid cols={2} gap="md">
                <Button
                  variant="outline"
                  className={`h-24 transition-all border-2 bg-muted-foreground/20 hover:bg-muted-foreground/30 text-white ${
                    gender === 'male' ? 'border-brand-primary bg-brand-primary/20' : 'border-muted-foreground'
                  }`}
                  onClick={() => handleGenderSelect('male')}
                >
                  <VStack gap="sm" align="center" justify="center" className="h-full">
                    <User className="w-8 h-8" />
                    <span className="font-medium">{t('onboarding.male')}</span>
                  </VStack>
                </Button>

                <Button
                  variant="outline"
                  className={`h-24 transition-all border-2 bg-muted-foreground/20 hover:bg-muted-foreground/30 text-white ${
                    gender === 'female' ? 'border-brand-primary bg-brand-primary/20' : 'border-muted-foreground'
                  }`}
                  onClick={() => handleGenderSelect('female')}
                >
                  <VStack gap="sm" align="center" justify="center" className="h-full">
                    <User className="w-8 h-8" />
                    <span className="font-medium">{t('onboarding.female')}</span>
                  </VStack>
                </Button>
              </Grid>
            </VStack>
          )}

          {step === 2 && gender && (
            <VStack gap="md">
              <h3 className="text-lg font-semibold text-center text-white mb-token-sm">
                {t('onboarding.showMeProfiles')}
              </h3>

              <VStack gap="sm">
                <Button
                  variant="outline"
                  className={`w-full h-16 flex items-center justify-between px-token-md transition-all border-2 bg-muted-foreground/20 hover:bg-muted-foreground/30 text-white ${
                    orientation === 'Hetero' ? 'border-brand-primary bg-brand-primary/20' : 'border-muted-foreground'
                  }`}
                  onClick={() => handleOrientationSelect('Hetero')}
                  disabled={isSubmitting}
                >
                  <HStack align="center" gap="sm">
                    <Users className="w-6 h-6" />
                    <span className="font-medium">{getOrientationLabel('Hetero')}</span>
                  </HStack>
                  <span className="text-sm text-muted">
                    {t('onboarding.heteroLabel')}
                  </span>
                </Button>

                <Button
                  variant="outline"
                  className={`w-full h-16 flex items-center justify-between px-token-md transition-all border-2 bg-muted-foreground/20 hover:bg-muted-foreground/30 text-white ${
                    orientation === 'Schwul' ? 'border-brand-primary bg-brand-primary/20' : 'border-muted-foreground'
                  }`}
                  onClick={() => handleOrientationSelect('Schwul')}
                  disabled={isSubmitting}
                >
                  <HStack align="center" gap="sm">
                    <Users className="w-6 h-6" />
                    <span className="font-medium">{getOrientationLabel('Schwul')}</span>
                  </HStack>
                  <span className="text-sm text-muted">
                    {gender === 'male' ? t('onboarding.gayLabel') : t('onboarding.lesbianLabel')}
                  </span>
                </Button>

                <Button
                  variant="outline"
                  className={`w-full h-16 flex items-center justify-between px-token-md transition-all border-2 bg-muted-foreground/20 hover:bg-muted-foreground/30 text-white ${
                    orientation === 'Bi' ? 'border-brand-primary bg-brand-primary/20' : 'border-muted-foreground'
                  }`}
                  onClick={() => handleOrientationSelect('Bi')}
                  disabled={isSubmitting}
                >
                  <HStack align="center" gap="sm">
                    <Users className="w-6 h-6" />
                    <span className="font-medium">{getOrientationLabel('Bi')}</span>
                  </HStack>
                  <span className="text-sm text-muted">
                    {t('onboarding.biLabel')}
                  </span>
                </Button>
              </VStack>

              {/* Back button */}
              <Button
                variant="ghost"
                className="w-full mt-token-md text-muted hover:text-white hover:bg-muted"
                onClick={() => setStep(1)}
                disabled={isSubmitting}
              >
                {t('onboarding.step1Title')}
              </Button>
            </VStack>
          )}
        </div>
      </div>
    </div>
  );
}
